import { useEffect, useState, useCallback, useRef } from "react";
import { toast } from "sonner";
import axios from "axios";
import { 
  Gamepad2,
  RefreshCw,
  Loader2,
  User,
  Phone,
  Mail,
  Copy,
  Camera,
  ExternalLink,
  ChevronDown,
  Check,
  CreditCard,
  History,
  ArrowDownCircle,
  ArrowUpCircle,
  Download
} from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const TEMPLATES = [
  { id: 'personal', name: 'Персональные данные', icon: User, description: 'Профиль пользователя' },
  { id: 'payments', name: 'История платежей', icon: History, description: 'Транзакции и операции' }
];

export default function BetboomTemplate() {
  const [profiles, setProfiles] = useState([]);
  const [selectedProfile, setSelectedProfile] = useState(null);
  const [selectedTemplate, setSelectedTemplate] = useState('personal');
  const [isLoading, setIsLoading] = useState(true);
  const [isGenerating, setIsGenerating] = useState(false);
  const [generatedData, setGeneratedData] = useState(null);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [mixOperations, setMixOperations] = useState(false);
  const [showBrowser, setShowBrowser] = useState(true);
  const [browserTheme, setBrowserTheme] = useState('dark'); // 'dark' or 'light'
  const [isDownloadingArchive, setIsDownloadingArchive] = useState(false);
  const previewRef = useRef(null);

  const getAuthHeaders = () => {
    const token = localStorage.getItem("token");
    return { Authorization: `Bearer ${token}` };
  };

  const fetchProfiles = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/ozon/profiles`, {
        headers: getAuthHeaders(),
      });
      // Add random betboomId for each profile
      const profilesWithIds = response.data.map(profile => ({
        ...profile,
        betboomId: profile.betboomId || Math.floor(100000 + Math.random() * 900000)
      }));
      setProfiles(profilesWithIds);
    } catch (error) {
      console.error("Error fetching profiles:", error);
      toast.error("Ошибка загрузки профилей");
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchProfiles();
  }, [fetchProfiles]);

  // Convert OZON category to payment method
  const getPaymentMethod = (category) => {
    if (!category) return 'СБП';
    const cat = category.toLowerCase();
    if (cat.includes('пополнен')) return 'СБП';
    if (cat.includes('перевод')) return 'СБП';
    if (cat.includes('финанс')) return 'Карта';
    if (['супермаркет', 'фастфуд', 'маркетплейс', 'развлечен', 'магазин', 'одежда', 'электроник', 'аптек', 'топливо', 'транспорт', 'связь', 'отел'].some(x => cat.includes(x))) return 'Карта';
    return 'СБП';
  };

  // Generate random casino operations
  const generateCasinoOperation = (dateStr) => {
    const operations = [
      { desc: 'Выигрыш в слотах', type: 'deposit' },
      { desc: 'Ставка на спорт', type: 'withdrawal' },
      { desc: 'Джекпот', type: 'deposit' },
      { desc: 'Бонус за депозит', type: 'deposit' },
      { desc: 'Кэшбэк', type: 'deposit' },
      { desc: 'Ставка в казино', type: 'withdrawal' },
    ];
    const op = operations[Math.floor(Math.random() * operations.length)];
    const amount = Math.floor(Math.random() * 8000) + 500;
    
    return {
      id: Date.now() + Math.random() * 10000,
      type: op.type,
      category: 'Казино',
      method: 'Игра',
      amount,
      date: dateStr?.split(',')[0] || '',
      time: dateStr?.split(',')[1]?.trim() || '',
      description: op.desc
    };
  };

  // Parse date string
  const parseDate = (dateStr) => {
    if (!dateStr) return new Date(0);
    const match = dateStr.match(/(\d{2})\.(\d{2})\.(\d{4})/);
    if (match) {
      const [, day, month, year] = match;
      return new Date(year, month - 1, day);
    }
    return new Date(0);
  };

  // Generate date between two dates
  const generateDateBetween = (date1Str, date2Str) => {
    const date1 = parseDate(date1Str);
    const date2 = parseDate(date2Str);
    const minTime = Math.min(date1.getTime(), date2.getTime());
    const maxTime = Math.max(date1.getTime(), date2.getTime());
    const randomTime = minTime + Math.random() * (maxTime - minTime);
    const date = new Date(randomTime);
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    const hour = String(Math.floor(Math.random() * 14) + 8).padStart(2, '0');
    const min = String(Math.floor(Math.random() * 60)).padStart(2, '0');
    return `${day}.${month}.${year}, ${hour}:${min}`;
  };

  const handleGenerate = () => {
    if (!selectedProfile) {
      toast.error("Выберите профиль OZON");
      return;
    }
    
    setIsGenerating(true);
    
    // Generate data from profile
    const firstName = selectedProfile.first_name || selectedProfile.name?.split(' ')[0] || "Имя";
    const lastName = selectedProfile.last_name || selectedProfile.name?.split(' ')[1] || "Фамилия";
    const phone = selectedProfile.phone || "+7 (999) 123-45-67";
    const email = selectedProfile.email !== "не установлен" ? selectedProfile.email : `${firstName.toLowerCase()}.${lastName.toLowerCase()}@mail.ru`;
    const gender = selectedProfile.gender || "Мужской";
    const city = selectedProfile.city || "Москва";
    
    // Process transactions for payments template
    let ozonTransactions = (selectedProfile.transactions || []).map(tx => ({
      ...tx,
      method: getPaymentMethod(tx.category),
      isOzonTx: true  // Mark as original OZON transaction
    }));
    
    // Sort by date
    ozonTransactions.sort((a, b) => {
      const dateA = parseDate(a.date);
      const dateB = parseDate(b.date);
      return dateB - dateA;
    });
    
    let finalTransactions = [];
    
    // Mix with random casino operations ONLY if checkbox is enabled
    if (mixOperations && ozonTransactions.length > 0) {
      // Add all OZON transactions first
      finalTransactions = ozonTransactions.map(tx => ({ ...tx }));
      
      // Add exactly 5-6 random casino operations
      const numCasinoOps = Math.floor(Math.random() * 2) + 5; // 5 or 6
      
      // Get date range from OZON transactions
      const dates = ozonTransactions.map(t => {
        const parts = t.date.split('.');
        return new Date(parseInt(parts[2]), parseInt(parts[1]) - 1, parseInt(parts[0]));
      });
      const minDate = new Date(Math.min(...dates));
      const maxDate = new Date(Math.max(...dates));
      
      for (let i = 0; i < numCasinoOps; i++) {
        // Random date between min and max
        const randomTime = minDate.getTime() + Math.random() * (maxDate.getTime() - minDate.getTime());
        const randomDate = new Date(randomTime);
        const dateStr = `${String(randomDate.getDate()).padStart(2, '0')}.${String(randomDate.getMonth() + 1).padStart(2, '0')}.${randomDate.getFullYear()}`;
        finalTransactions.push(generateCasinoOperation(dateStr));
      }
      
      // Sort by date (newest first)
      finalTransactions.sort((a, b) => {
        const parseDate = (d) => {
          const parts = d.split('.');
          return new Date(parseInt(parts[2]), parseInt(parts[1]) - 1, parseInt(parts[0]));
        };
        return parseDate(b.date) - parseDate(a.date);
      });
    } else {
      // No mixing - just copy OZON transactions as-is
      finalTransactions = ozonTransactions.map(tx => ({ ...tx }));
    }
    
    // ОБЯЗАТЕЛЬНО: Добавляем первое пополнение СБП в конец (самая ранняя дата)
    // Это всегда должно быть последней операцией (самой старой)
    const dates = finalTransactions.length > 0 ? finalTransactions.map(t => {
      const parts = t.date.split('.');
      return new Date(parseInt(parts[2]), parseInt(parts[1]) - 1, parseInt(parts[0]));
    }) : [new Date()];
    const earliestDate = new Date(Math.min(...dates));
    earliestDate.setDate(earliestDate.getDate() - Math.floor(Math.random() * 5) - 1); // 1-5 days before earliest
    
    const randomHour = String(Math.floor(Math.random() * 14) + 8).padStart(2, '0'); // 08-22
    const randomMin = String(Math.floor(Math.random() * 60)).padStart(2, '0');
    const firstDepositAmount = Math.floor(Math.random() * 8001) + 2000; // 2000-10000
    
    finalTransactions.push({
      id: Date.now() + 9999,
      type: 'deposit',
      category: 'Пополнения',
      method: 'СБП',
      amount: firstDepositAmount,
      date: `${String(earliestDate.getDate()).padStart(2, '0')}.${String(earliestDate.getMonth() + 1).padStart(2, '0')}.${earliestDate.getFullYear()}`,
      time: `${randomHour}:${randomMin}`,
      description: 'Пополнение по СБП',
      isFirstDeposit: true,  // Flag to prevent inversion in preview
      isOzonTx: false
    });
    
    const data = {
      firstName,
      lastName,
      fullName: `${firstName} ${lastName}`,
      phone: formatPhone(phone),
      email,
      gender,
      city,
      balance: selectedProfile.template_balance || selectedProfile.balance || 0,
      id: selectedProfile.id,
      betboomId: selectedProfile.betboomId,
      transactions: finalTransactions,
      template: selectedTemplate,
      mixOperations: mixOperations,
      showBrowser: showBrowser,
      browserTheme: browserTheme  // Pass browser theme
    };
    
    setGeneratedData(data);
    setIsGenerating(false);
    toast.success("Страница сгенерирована!");
  };

  const formatPhone = (phone) => {
    const cleaned = phone.replace(/\D/g, '');
    if (cleaned.length === 11) {
      return `+7 (${cleaned.slice(1,4)}) ${cleaned.slice(4,7)}-${cleaned.slice(7,9)}-${cleaned.slice(9,11)}`;
    }
    return phone;
  };

  const getPreviewUrl = () => {
    if (!generatedData) return '';
    const path = selectedTemplate === 'payments' ? '/templates/betboom/payments' : '/templates/betboom/preview';
    return `${window.location.origin}${path}?data=${encodeURIComponent(JSON.stringify(generatedData))}`;
  };

  const handleCopyLink = () => {
    if (!generatedData) return;
    const link = getPreviewUrl();
    navigator.clipboard.writeText(link);
    toast.success("Ссылка скопирована!");
  };

  const handleScreenshot = async () => {
    if (!generatedData) return;
    
    try {
      toast.info("Создание скриншота полной страницы...");
      
      // Build the preview URL based on selected template
      const previewUrl = getPreviewUrl();
      const templateName = selectedTemplate === 'payments' ? 'payments' : 'profile';
      
      const response = await axios.post(
        `${API}/screenshot`,
        {
          url: previewUrl,
          filename: `betboom_${templateName}_${generatedData.firstName}_${generatedData.lastName}.png`
        },
        { headers: getAuthHeaders() }
      );
      
      if (response.data.success) {
        // Convert base64 to blob and download
        const byteCharacters = atob(response.data.screenshot);
        const byteNumbers = new Array(byteCharacters.length);
        for (let i = 0; i < byteCharacters.length; i++) {
          byteNumbers[i] = byteCharacters.charCodeAt(i);
        }
        const byteArray = new Uint8Array(byteNumbers);
        const blob = new Blob([byteArray], { type: 'image/png' });
        
        const link = document.createElement('a');
        link.href = URL.createObjectURL(blob);
        link.download = response.data.filename;
        link.click();
        
        URL.revokeObjectURL(link.href);
        toast.success("Скриншот сохранён!");
      } else {
        toast.error(`Ошибка: ${response.data.error}`);
      }
    } catch (error) {
      console.error("Screenshot error:", error);
      toast.error("Ошибка создания скриншота");
    }
  };

  const handleOpenPreview = () => {
    if (!generatedData) return;
    const link = getPreviewUrl();
    window.open(link, '_blank');
  };

  const downloadArchive = async () => {
    if (!generatedData) return;
    
    setIsDownloadingArchive(true);
    toast.info("Создание архива скриншотов...");
    
    try {
      const response = await axios.post(
        `${API}/templates/betboom/archive`,
        { 
          data: generatedData,
          pages: ['personal', 'payments']
        },
        { 
          headers: getAuthHeaders(),
          responseType: 'blob'
        }
      );
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Betboom_${generatedData.betboomId}_${Date.now()}.zip`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success("Архив скриншотов скачан!");
    } catch (error) {
      console.error("Archive error:", error);
      toast.error("Ошибка создания архива");
    } finally {
      setIsDownloadingArchive(false);
    }
  };

  return (
    <div className="animate-fade-in" data-testid="betboom-template-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-3" style={{ fontFamily: 'Manrope, sans-serif' }}>
            <div className="w-10 h-10 bg-gradient-to-br from-yellow-500 to-orange-600 rounded-xl flex items-center justify-center shadow-lg">
              <Gamepad2 className="w-5 h-5 text-white" />
            </div>
            Казино Betboom
          </h1>
          <p className="text-slate-500 mt-1">
            Генератор персональной страницы из профиля OZON
          </p>
        </div>
        <Button 
          onClick={() => fetchProfiles()}
          variant="outline"
          data-testid="refresh-profiles-btn"
        >
          <RefreshCw className="w-4 h-4" />
          Обновить профили
        </Button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Left Panel - Profile Selection */}
        <Card>
          <CardContent className="p-6">
            <h2 className="text-lg font-semibold text-slate-800 mb-4">Настройки генерации</h2>
            
            {isLoading ? (
              <div className="flex justify-center py-8">
                <Loader2 className="w-8 h-8 animate-spin text-yellow-500" />
              </div>
            ) : (
              <>
                {/* Template Selection */}
                <div className="mb-6">
                  <Label className="mb-2 block">Шаблон</Label>
                  <div className="grid grid-cols-2 gap-3">
                    {TEMPLATES.map((template) => {
                      const Icon = template.icon;
                      const isSelected = selectedTemplate === template.id;
                      return (
                        <button
                          key={template.id}
                          onClick={() => {
                            setSelectedTemplate(template.id);
                            setGeneratedData(null);
                          }}
                          className={`p-4 rounded-lg border-2 transition-all text-left ${
                            isSelected 
                              ? 'border-yellow-500 bg-yellow-50' 
                              : 'border-slate-200 hover:border-yellow-300'
                          }`}
                        >
                          <div className="flex items-center gap-2 mb-1">
                            <Icon className={`w-5 h-5 ${isSelected ? 'text-yellow-600' : 'text-slate-400'}`} />
                            <span className={`font-medium text-sm ${isSelected ? 'text-yellow-700' : 'text-slate-700'}`}>
                              {template.name}
                            </span>
                          </div>
                          <p className="text-xs text-slate-400">{template.description}</p>
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* Profile Dropdown */}
                <div className="mb-6">
                  <Label className="mb-2 block">Профиль OZON</Label>
                  <div className="relative">
                    <button
                      onClick={() => setDropdownOpen(!dropdownOpen)}
                      className="w-full flex items-center justify-between p-3 border rounded-lg bg-white hover:border-yellow-500 transition-colors"
                      data-testid="profile-dropdown"
                    >
                      {selectedProfile ? (
                        <div className="flex items-center gap-3">
                          <div className={`w-3 h-3 rounded-full ${selectedProfile.is_blocked ? 'bg-red-500' : 'bg-green-500'}`} />
                          <div>
                            <span className="font-medium">{selectedProfile.name || selectedProfile.dolphin_profile_name}</span>
                            <div className="text-xs text-slate-400">
                              ID: {selectedProfile.betboomId} • {selectedProfile.phone}
                            </div>
                          </div>
                        </div>
                      ) : (
                        <span className="text-slate-400">Выберите профиль...</span>
                      )}
                      <ChevronDown className={`w-5 h-5 text-slate-400 transition-transform ${dropdownOpen ? 'rotate-180' : ''}`} />
                    </button>
                    
                    {dropdownOpen && (
                      <div className="absolute z-10 w-full mt-1 bg-white border rounded-lg shadow-lg max-h-60 overflow-y-auto">
                        {profiles.length === 0 ? (
                          <div className="p-4 text-center text-slate-400">
                            Нет профилей. Добавьте профиль в разделе OZON.
                          </div>
                        ) : (
                          profiles.map((profile) => (
                            <button
                              key={profile.id}
                              onClick={() => {
                                setSelectedProfile(profile);
                                setDropdownOpen(false);
                                setGeneratedData(null);
                              }}
                              className="w-full flex items-center gap-3 p-3 hover:bg-slate-50 transition-colors text-left"
                            >
                              <div className={`w-3 h-3 rounded-full ${profile.is_blocked ? 'bg-red-500' : 'bg-green-500'}`} />
                              <div className="flex-1">
                                <div className="font-medium text-slate-800">
                                  {profile.name || profile.dolphin_profile_name}
                                </div>
                                <div className="text-sm text-slate-400">
                                  ID: {profile.betboomId} • {profile.phone} • {profile.balance?.toLocaleString() || 0} ₽
                                </div>
                              </div>
                              {selectedProfile?.id === profile.id && (
                                <Check className="w-5 h-5 text-green-500" />
                              )}
                            </button>
                          ))
                        )}
                      </div>
                    )}
                  </div>
                </div>

                {/* Mix Operations Checkbox */}
                <div className="flex items-center gap-3 mb-4 p-3 bg-slate-50 rounded-lg">
                  <input
                    type="checkbox"
                    id="mixOperations"
                    checked={mixOperations}
                    onChange={(e) => setMixOperations(e.target.checked)}
                    className="w-4 h-4 rounded border-slate-300 text-yellow-600 focus:ring-yellow-500"
                  />
                  <label htmlFor="mixOperations" className="text-sm text-slate-700 cursor-pointer">
                    Перемешивать операции (добавить рандомные казино-операции)
                  </label>
                </div>

                {/* Show Browser Checkbox */}
                <div className="flex items-center gap-3 mb-4 p-3 bg-slate-50 rounded-lg">
                  <input
                    type="checkbox"
                    id="showBrowser"
                    checked={showBrowser}
                    onChange={(e) => setShowBrowser(e.target.checked)}
                    className="w-4 h-4 rounded border-slate-300 text-yellow-600 focus:ring-yellow-500"
                  />
                  <label htmlFor="showBrowser" className="text-sm text-slate-700 cursor-pointer">
                    Показывать рамку браузера
                  </label>
                </div>

                {/* Browser Theme Selection */}
                <div className="mb-6 p-3 bg-slate-50 rounded-lg">
                  <Label className="mb-2 block text-sm text-slate-700">Фон браузера</Label>
                  <div className="flex gap-3">
                    <button
                      onClick={() => setBrowserTheme('light')}
                      className={`flex-1 py-2 px-3 rounded-lg border-2 text-sm font-medium transition-all ${
                        browserTheme === 'light'
                          ? 'border-yellow-500 bg-white text-slate-800'
                          : 'border-slate-200 bg-white text-slate-500 hover:border-yellow-300'
                      }`}
                    >
                      Светлый
                    </button>
                    <button
                      onClick={() => setBrowserTheme('dark')}
                      className={`flex-1 py-2 px-3 rounded-lg border-2 text-sm font-medium transition-all ${
                        browserTheme === 'dark'
                          ? 'border-yellow-500 bg-slate-800 text-white'
                          : 'border-slate-200 bg-slate-700 text-slate-300 hover:border-yellow-300'
                      }`}
                    >
                      Тёмный
                    </button>
                  </div>
                </div>

                <Button
                  onClick={handleGenerate}
                  disabled={!selectedProfile || isGenerating}
                  className="w-full bg-yellow-500 hover:bg-yellow-600 text-black font-semibold"
                  data-testid="generate-btn"
                >
                  {isGenerating ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin mr-2" />
                      Генерация...
                    </>
                  ) : (
                    'Сформировать страницу'
                  )}
                </Button>
              </>
            )}
          </CardContent>
        </Card>

        {/* Right Panel - Preview & Actions */}
        <Card>
          <CardContent className="p-6">
            <h2 className="text-lg font-semibold text-slate-800 mb-4">Предпросмотр</h2>
            
            {!generatedData ? (
              <div className="flex flex-col items-center justify-center h-64 text-center">
                <Gamepad2 className="w-16 h-16 text-slate-200 mb-4" />
                <div className="text-slate-400">
                  Выберите профиль и нажмите<br />"Сформировать страницу"
                </div>
              </div>
            ) : (
              <div className="space-y-6">
                <div className="flex items-center gap-3 pb-4 border-b border-slate-200">
                  <div className="w-12 h-12 rounded-full bg-gradient-to-br from-yellow-500 to-orange-600 flex items-center justify-center">
                    <User className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <div className="text-xl font-semibold text-slate-800">{generatedData.fullName}</div>
                    <div className="text-yellow-600 font-medium">
                      {generatedData.balance?.toLocaleString() || 0} ₽
                    </div>
                  </div>
                </div>

                <div className="space-y-4">
                  <div className="flex items-center gap-3 text-slate-600">
                    <Phone className="w-4 h-4 text-slate-400" />
                    <span>{generatedData.phone}</span>
                  </div>
                  <div className="flex items-center gap-3 text-slate-600">
                    <Mail className="w-4 h-4 text-slate-400" />
                    <span>{generatedData.email}</span>
                  </div>
                  {selectedTemplate === 'payments' && (
                    <div className="flex items-center gap-3 text-slate-600">
                      <History className="w-4 h-4 text-slate-400" />
                      <span>{generatedData.transactions?.length || 0} транзакций</span>
                    </div>
                  )}
                </div>

                <div className="flex gap-3 pt-4">
                  <Button
                    data-testid="open-preview-btn"
                    onClick={handleOpenPreview}
                    className="flex-1 bg-yellow-500 hover:bg-yellow-600 text-black gap-2"
                  >
                    <ExternalLink className="w-4 h-4" />
                    Открыть
                  </Button>
                  <Button
                    data-testid="copy-link-btn"
                    variant="outline"
                    onClick={handleCopyLink}
                    className="gap-2"
                  >
                    <Copy className="w-4 h-4" />
                    Копировать
                  </Button>
                  <Button
                    variant="outline"
                    onClick={downloadArchive}
                    disabled={isDownloadingArchive}
                    className="gap-2 text-green-600 border-green-200 hover:bg-green-50"
                  >
                    {isDownloadingArchive ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <Download className="w-4 h-4" />
                    )}
                    Архив
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
